import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PageHeader } from '../components/AppLayout';
import { Avatar } from '../components/Logo';
import { useIsMobile } from '../hooks/useIsMobile';
import MobileMessagesPage from './MobileMessagesPage';
import { formatRelative } from '../constants/users';
import { ArrowLeft, Send, MessageSquare, Plus, X, Users as UsersIcon, Loader2, Archive, Trash2, Camera, Video, Briefcase, Star, Heart, Flame, Palette, Music2, MoreVertical, Pencil, Search, UserPlus, Check } from '../components/icons';
import { ClientTreeView } from '../components/ClientTreeView';

// Community icons (subset suitable for "rooms")
const COMMUNITY_ICONS = {
  users: UsersIcon,
  camera: Camera,
  video: Video,
  briefcase: Briefcase,
  star: Star,
  heart: Heart,
  flame: Flame,
  palette: Palette,
  music: Music2,
};
const COMMUNITY_ICON_KEYS = Object.keys(COMMUNITY_ICONS);
const COMMUNITY_COLORS = ['#5BBEFC', '#A78BFA', '#34D399', '#F5A623', '#EF4444', '#EC4899', '#10B981'];

export default function MessagesPage() {
  const isMobile = useIsMobile();
  if (isMobile) return <MobileMessagesPage />;
  return <MessagesPageDesktop />;
}

function MessagesPageDesktop() {
  const { user, isFounder } = useAuth();
  const [convs, setConvs] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [users, setUsers] = useState([]);
  const [active, setActive] = useState(null); // conversation object
  const [showNew, setShowNew] = useState(false);
  const [showNewCom, setShowNewCom] = useState(false);
  const [assignConvId, setAssignConvId] = useState(null);  // {convId} for community-assign mini-modal
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');  // all | groups | admin | monteur
  const [activeCommunity, setActiveCommunity] = useState(null); // community_id or null
  const [search, setSearch] = useState(''); // Iter47s — search across communities + convs
  const [manageConv, setManageConv] = useState(null); // {conv} for rename/add-member/delete menu
  const [clientPickerOpen, setClientPickerOpen] = useState(false); // Iter 62aw
  const [pickedClientForNewGroup, setPickedClientForNewGroup] = useState(null); // Iter 62aw
  const [searchParams, setSearchParams] = useSearchParams();
  // Iter 62av — Vue "Clients" (regroupée par client) ou "Messages privés" (DMs).
  // Iter 62bz — Nouveau tab "Groupes" qui liste TOUS les groupes créés (avec ou sans client).
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('asa_msg_view_mode');
    if (saved === 'tree' || saved === 'dm' || saved === 'groups') return saved;
    return 'tree';
  });
  useEffect(() => { localStorage.setItem('asa_msg_view_mode', viewMode); }, [viewMode]);

  const reload = async () => {
    const [c, u, com] = await Promise.all([
      api.get('/conversations'),
      api.get('/users'),
      api.get('/communities'),
    ]);
    setConvs(c.data);
    setUsers(u.data.filter((x) => x.id !== user.id));
    setCommunities(com.data || []);
    setLoading(false);
    return c.data;
  };

  useEffect(() => {
    (async () => {
      const list = await reload();
      // Iter62z — Deep-link support: ?conv=<id> or ?peer=<user_id>
      const convParam = searchParams.get('conv');
      const peerParam = searchParams.get('peer');
      const pendingId = localStorage.getItem('asa_open_conv');
      let target = null;
      if (convParam) target = list.find((c) => c.id === convParam);
      if (!target && peerParam) {
        // Iter62z — DM conversations are synthesized as id='dm:<peer_id>' by /conversations
        target = list.find((c) => c.id === `dm:${peerParam}` || (c.type === 'dm' && c.with?.id === peerParam));
      }
      if (!target && pendingId) {
        localStorage.removeItem('asa_open_conv');
        target = list.find((c) => c.id === pendingId);
      }
      if (target) setActive(target);
      if (convParam || peerParam) {
        const next = new URLSearchParams(searchParams);
        next.delete('conv'); next.delete('peer');
        setSearchParams(next, { replace: true });
      }
    })();
  /* eslint-disable-next-line */ }, []);

  // Iter47v — Refresh list after a community logo upload
  useEffect(() => {
    const handler = () => reload();
    window.addEventListener('asa-community-updated', handler);
    return () => window.removeEventListener('asa-community-updated', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drag-drop conv → community
  const dropConvOnCommunity = async (convId, communityId) => {
    try {
      await api.patch(`/conversations/${convId}/community`, { community_id: communityId });
      toast.success(communityId ? 'Ajouté à la communauté' : 'Retiré');
      reload();
    } catch (e) { toast.error('Erreur'); }
  };

  const deleteCommunity = async (cid, name) => {
    if (!window.confirm(`Supprimer la communauté « ${name} » ?\nLes conversations à l'intérieur seront déplacées dans « Sans ».`)) return;
    try {
      await api.delete(`/communities/${cid}`);
      if (activeCommunity === cid) setActiveCommunity(null);
      toast.success('Communauté supprimée');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  // Iter47s — WhatsApp "Communities" poupée russe UX:
  // Level 1 (activeCommunity === null): search + list of big community rows (NASH, KANVAS…)
  //   + list of orphan conversations (no community assigned)
  // Level 2 (activeCommunity set): back button + community header + list of convs INSIDE
  // (Removed the "Admin / Monteur / Groupes" tabs that Rayan called "trop compliqué".)
  const q = search.trim().toLowerCase();
  const filtered = convs.filter((c) => {
    if (activeCommunity === null) {
      // Level 1 — orphan conversations only (no community)
      if (c.community_id) return false;
    } else if (activeCommunity === 'none') {
      if (c.community_id) return false;
    } else {
      if (c.community_id !== activeCommunity) return false;
    }
    if (q) {
      const name = (c.type === 'group' ? c.name : c.members?.find((m) => m.id !== user.id)?.name || '') || '';
      if (!name.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const currentCommunity = activeCommunity && activeCommunity !== 'none'
    ? communities.find((c) => c.id === activeCommunity)
    : null;

  // Iter47s — Manage conv actions (rename / add member / delete)
  const renameConv = async (c, newName) => {
    if (!newName?.trim() || newName.trim() === c.name) return;
    try {
      await api.patch(`/conversations/${c.id}`, { name: newName.trim() });
      toast.success('Groupe renommé');
      setManageConv(null);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const addMember = async (c, uid) => {
    try {
      await api.post(`/conversations/${c.id}/members`, { user_id: uid });
      toast.success('Membre ajouté');
      setManageConv(null);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const deleteConv = async (c) => {
    if (!window.confirm(`Supprimer le groupe "${c.name}" ?\nCette action est irréversible.`)) return;
    try {
      await api.delete(`/conversations/${c.id}`);
      toast.success('Groupe supprimé');
      setManageConv(null);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  if (active) return (
    <Thread
      conv={active}
      currentUserId={user.id}
      onBack={() => { setActive(null); reload(); }}
    />
  );

  return (
    <div className="page-fade">
      <PageHeader
        title="Messagerie"
        subtitle="Clients & messages privés"
      />

      <div style={{ padding: '0 22px 100px' }}>
        {/* Iter 62av — Toggle Clients (groupés) / Messages privés (DM) */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
          <div
            data-testid="msg-view-toggle"
            style={{
              display: 'inline-flex', gap: 2, padding: 3, borderRadius: 999,
              background: 'var(--sf)', border: '1px solid var(--bd)',
            }}
          >
            <button
              data-testid="msg-view-tree"
              onClick={() => setViewMode('tree')}
              style={{
                padding: '8px 16px', borderRadius: 999, border: 'none',
                background: viewMode === 'tree' ? 'rgba(245,197,106,0.18)' : 'transparent',
                color: viewMode === 'tree' ? '#F5C56A' : 'var(--t2)',
              fontFamily: 'Sora', fontWeight: 800, fontSize: 12, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
              transition: 'all 0.15s',
            }}
          >
            <UsersIcon size={12} strokeWidth={2.2} /> Clients
          </button>
          <button
            data-testid="msg-view-dm"
            onClick={() => setViewMode('dm')}
            style={{
              padding: '8px 16px', borderRadius: 999, border: 'none',
              background: viewMode === 'dm' ? 'rgba(46,163,242,0.18)' : 'transparent',
              color: viewMode === 'dm' ? '#5BBEFC' : 'var(--t2)',
              fontFamily: 'Sora', fontWeight: 800, fontSize: 12, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
              transition: 'all 0.15s',
            }}
          >
            <MessageSquare size={12} strokeWidth={2.2} /> Messages privés
          </button>
          {/* Iter 62bz — Tab "Groupes" : liste tous les groupes créés (avec ou sans client) */}
          <button
            data-testid="msg-view-groups"
            onClick={() => setViewMode('groups')}
            style={{
              padding: '8px 16px', borderRadius: 999, border: 'none',
              background: viewMode === 'groups' ? 'rgba(147,112,219,0.20)' : 'transparent',
              color: viewMode === 'groups' ? '#B58BFF' : 'var(--t2)',
              fontFamily: 'Sora', fontWeight: 800, fontSize: 12, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
              transition: 'all 0.15s',
            }}
          >
            <UsersIcon size={12} strokeWidth={2.2} /> Groupes ({convs.filter((c) => c.type === 'group').length})
          </button>
        </div>

        {/* Iter 62aw — Bouton top-level "Créer un groupe client" / "Nouveau groupe interne"
            Iter 62bz — Bouton disponible aussi sur le tab "Groupes" */}
        {isFounder && (viewMode === 'tree' || viewMode === 'groups') && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {viewMode === 'tree' && (
              <button
                data-testid="msg-create-client-group"
                onClick={() => setClientPickerOpen(true)}
                style={{
                  padding: '10px 16px', borderRadius: 999,
                  background: 'linear-gradient(140deg, rgba(245,197,106,0.22) 0%, rgba(245,197,106,0.08) 100%)',
                  border: '1px solid rgba(245,197,106,0.55)',
                  color: '#F5C56A',
                  fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
                  cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  boxShadow: '0 6px 18px rgba(245,197,106,0.20)',
                }}
              >
                <Plus size={13} strokeWidth={2.4} /> Créer un groupe client
              </button>
            )}
            {/* Iter 62bs — Créer un groupe interne (sans client) */}
            <button
              data-testid="msg-create-internal-group"
              onClick={() => setShowNew(true)}
              style={{
                padding: '10px 16px', borderRadius: 999,
                background: 'linear-gradient(140deg, rgba(91,190,252,0.22) 0%, rgba(91,190,252,0.08) 100%)',
                border: '1px solid rgba(91,190,252,0.55)',
                color: '#5BBEFC',
                fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
                cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 8,
                boxShadow: '0 6px 18px rgba(91,190,252,0.20)',
              }}
            >
              <Plus size={13} strokeWidth={2.4} /> Nouveau groupe interne
            </button>
          </div>
        )}
        </div>

        {viewMode === 'tree' ? (
          <ClientTreeView
            isMobile={false}
            onOpenConversation={(conv) => setActive(conv)}
          />
        ) : viewMode === 'groups' ? (
          <GroupsList
            groups={convs.filter((c) => c.type === 'group')}
            users={users}
            search={search}
            setSearch={setSearch}
            onOpen={(conv) => setActive(conv)}
          />
        ) : (
          <DirectMessagesList
            convs={convs.filter((c) => c.type === 'dm')}
            search={search}
            setSearch={setSearch}
            onOpen={(conv) => setActive(conv)}
          />
        )}
      </div>

      {showNew && (
        <NewGroupDialog
          users={users}
          communities={communities}
          onClose={() => setShowNew(false)}
          onCreated={(g) => { setShowNew(false); reload(); setActive({ id: g.id, type: 'group', name: g.name, members: users.filter((u) => g.members.includes(u.id)) }); }}
        />
      )}

      {showNewCom && (
        <CommunityDialog
          onClose={() => setShowNewCom(false)}
          onSaved={() => { setShowNewCom(false); reload(); }}
        />
      )}

      {/* Iter 62aw — Client picker for top-level "Créer un groupe client" */}
      {clientPickerOpen && (
        <ClientPickerModal
          onClose={() => setClientPickerOpen(false)}
          onPick={(cli) => {
            setClientPickerOpen(false);
            setPickedClientForNewGroup(cli);
          }}
        />
      )}
      {pickedClientForNewGroup && (
        <CreateClientGroupModal
          client={pickedClientForNewGroup}
          users={users}
          onClose={() => setPickedClientForNewGroup(null)}
          onCreated={(g) => {
            setPickedClientForNewGroup(null);
            reload();
            window.dispatchEvent(new Event('asa-messages-refresh'));
            setActive({ id: g.id, type: 'group', name: g.name, members: users.filter((u) => (g.members || []).includes(u.id)) });
          }}
        />
      )}

      {assignConvId && (
        <AssignToCommunityDialog
          convId={assignConvId}
          communities={communities}
          currentCommunityId={convs.find((c) => c.id === assignConvId)?.community_id}
          onClose={() => setAssignConvId(null)}
          onSaved={() => { setAssignConvId(null); reload(); }}
        />
      )}

      {manageConv && (
        <ManageGroupDialog
          conv={manageConv}
          users={users}
          onClose={() => setManageConv(null)}
          onRename={renameConv}
          onAddMember={addMember}
          onDelete={deleteConv}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Iter 62av — DirectMessagesList : liste des DMs (côté "Messages privés")
// ---------------------------------------------------------------------------
function DirectMessagesList({ convs, search, setSearch, onOpen }) {
  const q = (search || '').toLowerCase().trim();
  const filtered = q
    ? convs.filter((c) => (c.name || c.with?.name || '').toLowerCase().includes(q))
    : convs;
  return (
    <>
      <div
        data-testid="dm-search"
        className="asa-card"
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '11px 16px', marginBottom: 14, borderRadius: 12,
          background: 'var(--sf)', border: '1.5px solid var(--bd)',
        }}
      >
        <Search size={15} color="var(--t3)" strokeWidth={1.7} />
        <input
          data-testid="dm-search-input"
          placeholder="Rechercher un membre…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1, background: 'transparent', border: 'none', outline: 'none',
            fontFamily: 'Lexend', fontSize: 13, color: 'var(--t1)',
          }}
        />
        {search && <button onClick={() => setSearch('')}><X size={13} color="var(--t3)" /></button>}
      </div>

      {filtered.length === 0 ? (
        <div style={{
          padding: '40px 24px', textAlign: 'center',
          border: '1px dashed var(--bd)', borderRadius: 14,
          color: 'var(--t3)', fontFamily: 'Sora', fontSize: 13,
        }}>
          {q ? 'Aucun résultat' : 'Aucun message privé'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {filtered.map((c) => (
            <ConvRow
              key={c.id}
              conv={c}
              onClick={() => onOpen(c)}
            />
          ))}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Iter 62bz — GroupsList : ALL groups (client or internal). Simple tab UX.
// ---------------------------------------------------------------------------
function GroupsList({ groups, users, search, setSearch, onOpen }) {
  const q = (search || '').toLowerCase().trim();
  const [resetting, setResetting] = useState(false);
  const doReset = async () => {
    if (!window.confirm(`Supprimer TOUS les ${groups.length} groupes ? Cette action est IRRÉVERSIBLE.\nLes DMs seront conservés.`)) return;
    setResetting(true);
    try {
      const r = await api.post('/admin/messaging/reset-groups');
      toast.success(`${r.data.deleted_groups} groupes supprimés · ${r.data.deleted_messages} messages`);
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setResetting(false); }
  };
  const filtered = (q
    ? groups.filter((c) => (c.name || '').toLowerCase().includes(q))
    : groups
  ).slice().sort((a, b) =>
    (b.last?.created_at || b.updated_at || '').localeCompare(a.last?.created_at || a.updated_at || '')
  );
  const usersById = useMemo(() => {
    const m = {};
    (users || []).forEach((u) => { m[u.id] = u; });
    return m;
  }, [users]);
  return (
    <>
      <div
        data-testid="groups-search"
        className="asa-card"
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '11px 16px', marginBottom: 14, borderRadius: 12,
          background: 'var(--sf)', border: '1.5px solid var(--bd)',
        }}
      >
        <Search size={15} color="var(--t3)" strokeWidth={1.7} />
        <input
          data-testid="groups-search-input"
          placeholder="Rechercher un groupe…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1, background: 'transparent', border: 'none', outline: 'none',
            fontFamily: 'Lexend', fontSize: 13, color: 'var(--t1)',
          }}
        />
        {search && <button onClick={() => setSearch('')}><X size={13} color="var(--t3)" /></button>}
      </div>

      {/* Iter 62cb — Reset all groups (danger) */}
      {groups.length > 0 && (
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            data-testid="reset-all-groups"
            onClick={doReset}
            disabled={resetting}
            style={{
              padding: '7px 12px', borderRadius: 999,
              background: 'rgba(255,107,107,0.10)', border: '1px solid rgba(255,107,107,0.45)',
              color: '#FF6B6B', fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
              cursor: resetting ? 'wait' : 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            <Trash2 size={12} strokeWidth={2.2} /> {resetting ? 'Suppression…' : `Supprimer tous les ${groups.length} groupes`}
          </button>
        </div>
      )}


      {filtered.length === 0 ? (
        <div style={{
          padding: '40px 24px', textAlign: 'center',
          border: '1px dashed var(--bd)', borderRadius: 14,
          color: 'var(--t3)', fontFamily: 'Sora', fontSize: 13,
        }}>
          {q ? 'Aucun résultat' : 'Aucun groupe pour le moment. Clique sur « Nouveau groupe interne » pour en créer un.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {filtered.map((g) => (
            <button
              key={g.id}
              data-testid={`groups-list-item-${g.id}`}
              onClick={() => onOpen(g)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 14px', borderRadius: 12,
                background: 'var(--sf)', border: '1.5px solid var(--bd)',
                cursor: 'pointer', textAlign: 'left',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--sf2)'; e.currentTarget.style.borderColor = 'rgba(181,139,255,0.4)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--sf)'; e.currentTarget.style.borderColor = 'var(--bd)'; }}
            >
              <div style={{
                width: 40, height: 40, borderRadius: 12,
                background: g.color ? `${g.color}22` : 'rgba(181,139,255,0.15)',
                border: `1.5px solid ${g.color || 'rgba(181,139,255,0.4)'}`,
                display: 'grid', placeItems: 'center', flexShrink: 0,
              }}>
                <UsersIcon size={16} color={g.color || '#B58BFF'} strokeWidth={2} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                  <div style={{
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 14, color: 'var(--t1)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{g.name}</div>
                  {g.client_id && (
                    <span style={{
                      padding: '2px 7px', borderRadius: 6,
                      background: 'rgba(245,197,106,0.15)', border: '1px solid rgba(245,197,106,0.4)',
                      color: '#F5C56A', fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5,
                      letterSpacing: '0.05em', textTransform: 'uppercase',
                    }}>Client</span>
                  )}
                  {!g.client_id && (
                    <span style={{
                      padding: '2px 7px', borderRadius: 6,
                      background: 'rgba(91,190,252,0.15)', border: '1px solid rgba(91,190,252,0.4)',
                      color: '#5BBEFC', fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5,
                      letterSpacing: '0.05em', textTransform: 'uppercase',
                    }}>Interne</span>
                  )}
                </div>
                <div style={{
                  fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {(g.members || []).length} membre{(g.members || []).length > 1 ? 's' : ''}
                  {g.members && g.members.length > 0 && (
                    <> · {(g.members || []).slice(0, 3).map((m) => {
                      // Iter 62bz — /conversations enriches members as objects; also fallback to id string
                      if (typeof m === 'string') return usersById[m]?.name || m;
                      return m?.name || usersById[m?.id]?.name || m?.id || '';
                    }).join(', ')}
                    {g.members.length > 3 && ` +${g.members.length - 3}`}</>
                  )}
                </div>
                {g.last?.text && (
                  <div style={{
                    fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)', marginTop: 3,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    fontStyle: 'italic',
                  }}>{g.last.text.slice(0, 80)}</div>
                )}
              </div>
              {(g.unread_count || 0) > 0 && (
                <span style={{
                  minWidth: 22, height: 22, borderRadius: 11,
                  padding: '0 7px', background: '#EF4444', color: '#fff',
                  fontFamily: 'Sora', fontWeight: 800, fontSize: 11,
                  display: 'grid', placeItems: 'center', flexShrink: 0,
                }}>{g.unread_count}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </>
  );
}





// ---------------------------------------------------------------------------
// CommunityCircle — circular avatar in horizontal strip (drop target for group convs)
// ---------------------------------------------------------------------------
function CommunityCircle({ label, color, icon, count, active, onClick, testId, communityId, onDropConv, onDelete }) {
  const Icon = COMMUNITY_ICONS[icon] || UsersIcon;
  const [over, setOver] = useState(false);
  const [hover, setHover] = useState(false);
  const droppable = !!onDropConv;
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ position: 'relative', flexShrink: 0 }}
    >
      <button
        onClick={onClick}
        data-testid={testId}
        onDragOver={droppable ? (e) => { e.preventDefault(); setOver(true); e.dataTransfer.dropEffect = 'move'; } : undefined}
        onDragLeave={droppable ? () => setOver(false) : undefined}
        onDrop={droppable ? (e) => {
          e.preventDefault(); setOver(false);
          const cid = e.dataTransfer.getData('conv-id');
          if (cid) onDropConv(cid, communityId);
        } : undefined}
        style={{
          width: 60,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
          background: 'transparent', border: 'none', cursor: 'pointer',
          paddingTop: 2,
        }}
      >
        <div style={{
          position: 'relative',
          width: 52, height: 52, borderRadius: '50%',
          background: over ? color : (active ? color : `${color}1F`),
          border: `${over ? 3 : 2}px solid ${active || over ? color : `${color}66`}`,
          display: 'grid', placeItems: 'center',
          transition: 'all 0.18s',
          boxShadow: active ? `0 0 0 3px ${color}33` : (over ? `0 0 0 5px ${color}55` : 'none'),
          transform: over ? 'scale(1.10)' : 'scale(1)',
        }}>
          <Icon size={20} color={(active || over) ? '#fff' : color} strokeWidth={1.8} />
          {count > 0 && (
            <span style={{
              position: 'absolute', top: -3, right: -3,
              minWidth: 17, height: 17, padding: '0 5px',
              background: 'var(--r)', borderRadius: 999,
              border: '2px solid var(--bg)',
              fontFamily: 'Sora', fontWeight: 800, fontSize: 9.5, color: '#fff',
              display: 'grid', placeItems: 'center',
            }}>{count > 99 ? '99+' : count}</span>
          )}
        </div>
        <div style={{
          fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
          color: (active || over) ? 'var(--t1)' : 'var(--t3)',
          textAlign: 'center', maxWidth: 60,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{label}</div>
      </button>
      {onDelete && (
        <button
          data-testid={`community-delete-${communityId}`}
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          title="Supprimer cette communauté"
          style={{
            position: 'absolute', top: 0, right: 4,
            width: 18, height: 18, borderRadius: '50%',
            background: '#FF6B6B', border: '2px solid var(--bg)',
            display: 'grid', placeItems: 'center',
            cursor: 'pointer', zIndex: 2,
            opacity: hover ? 1 : 0.55,
            transition: 'opacity 0.15s, transform 0.15s',
            transform: hover ? 'scale(1.1)' : 'scale(1)',
          }}
        >
          <X size={9} color="#fff" strokeWidth={3} />
        </button>
      )}
    </div>
  );
}

function ConvRow({ conv, community, onClick, onAssign, onManage }) {
  const isGroup = conv.type === 'group';
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div
      className="asa-card row-card"
      onClick={onClick}
      data-testid={`conv-row-${conv.id}`}
      draggable={isGroup}
      onDragStart={isGroup ? (e) => {
        e.dataTransfer.setData('conv-id', conv.id);
        e.dataTransfer.effectAllowed = 'move';
      } : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: 14, padding: 14,
        textAlign: 'left',
        borderLeft: community ? `3px solid ${community.color}` : undefined,
        cursor: 'pointer',
        position: 'relative',
        zIndex: menuOpen ? 502 : 'auto',
      }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
        data-testid={`conv-${conv.id}`}
        style={{
          flex: 1, display: 'flex', alignItems: 'center', gap: 14,
          background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer',
          minWidth: 0, padding: 0,
        }}
      >
        {isGroup ? (
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: community ? `${community.color}14` : 'rgba(167,139,250,0.12)',
            border: `1.5px solid ${community ? `${community.color}55` : 'rgba(167,139,250,0.4)'}`,
            display: 'grid', placeItems: 'center', flexShrink: 0,
          }}>
            <UsersIcon size={17} color={community ? community.color : '#A78BFA'} strokeWidth={1.7} />
          </div>
        ) : (
          <Avatar user={conv.with} size={44} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: '0.92rem', color: 'var(--t1)' }}>
              {conv.name}
            </span>
            {isGroup && (
              <span style={{
                fontFamily: 'Sora', fontWeight: 600, fontSize: '0.58rem',
                color: '#A78BFA', padding: '2px 7px', borderRadius: 999,
                background: 'rgba(167,139,250,0.12)', textTransform: 'uppercase', letterSpacing: '0.06em',
              }}>Groupe · {conv.members?.length || 0}</span>
            )}
          </div>
          <div style={{
            fontFamily: 'Lexend', fontSize: '0.78rem', color: 'var(--t3)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 3,
          }}>
            {conv.last?.text || 'Aucun message — démarrer la conversation'}
          </div>
        </div>
        {conv.last && (
          <div style={{ fontFamily: 'Lexend', fontSize: '0.66rem', color: 'var(--t3)' }}>
            {formatRelative(conv.last.created_at)}
          </div>
        )}
      </button>

      {/* Iter47s — Manage menu (rename / add member / delete / community assign) */}
      {(onManage || onAssign) && (
        <div style={{ position: 'relative' }}>
          <button
            onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
            data-testid={`conv-${conv.id}-menu`}
            title="Options"
            style={{
              padding: 7, borderRadius: 8,
              background: 'var(--sf2)', border: '1.5px solid var(--bd)',
              cursor: 'pointer',
            }}
          >
            <MoreVertical size={13} color="var(--t3)" />
          </button>
          {menuOpen && (
            <>
              <div
                onClick={(e) => { e.stopPropagation(); setMenuOpen(false); }}
                style={{ position: 'fixed', inset: 0, zIndex: 500 }}
              />
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute', right: 0, top: '110%', zIndex: 501,
                  background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 10,
                  padding: 4, minWidth: 200, boxShadow: '0 12px 32px rgba(0,0,0,0.4)',
                }}
              >
                {onManage && (
                  <MenuItem
                    testid={`conv-${conv.id}-rename`}
                    icon={<Pencil size={12} />}
                    label="Renommer le groupe"
                    onClick={() => { setMenuOpen(false); onManage('rename'); }}
                  />
                )}
                {onManage && (
                  <MenuItem
                    testid={`conv-${conv.id}-add-member`}
                    icon={<UserPlus size={12} />}
                    label="Ajouter un membre"
                    onClick={() => { setMenuOpen(false); onManage('add-member'); }}
                  />
                )}
                {onAssign && (
                  <MenuItem
                    testid={`conv-${conv.id}-assign`}
                    icon={<UsersIcon size={12} />}
                    label="Affecter à une communauté"
                    onClick={() => { setMenuOpen(false); onAssign(); }}
                  />
                )}
                {onManage && (
                  <MenuItem
                    testid={`conv-${conv.id}-delete`}
                    icon={<Trash2 size={12} />}
                    label="Supprimer le groupe"
                    danger
                    onClick={() => { setMenuOpen(false); onManage('delete'); }}
                  />
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function MenuItem({ testid, icon, label, danger, onClick }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 9,
        padding: '9px 10px', borderRadius: 7,
        background: 'transparent', border: 'none',
        color: danger ? '#FF6B6B' : 'var(--t1)',
        fontFamily: 'Sora', fontWeight: 600, fontSize: 12,
        cursor: 'pointer', textAlign: 'left',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = danger ? 'rgba(255,107,107,0.10)' : 'var(--sf2)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >{icon} {label}</button>
  );
}

// ---------------------------------------------------------------------------
// Iter47s — CommunityRow: WhatsApp-style big row (vertical list) for level 1
// Iter47v — Click on the logo circle to upload a custom image
// ---------------------------------------------------------------------------
function CommunityRow({ com, count, onOpen, onDelete, onDropConv }) {
  const Icon = COMMUNITY_ICONS[com.icon] || UsersIcon;
  const [over, setOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const droppable = !!onDropConv;

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Format non supporté (image seulement)');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Fichier trop lourd (max 2 Mo)');
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.patch(`/communities/${com.id}`, { logo: dataUrl });
      toast.success('Logo mis à jour');
      // Force parent reload via a hard refresh (community list will refetch on next mount)
      window.dispatchEvent(new CustomEvent('asa-community-updated'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      data-testid={`community-row-${com.id}`}
      className="asa-card row-card"
      onDragOver={droppable ? (e) => { e.preventDefault(); setOver(true); e.dataTransfer.dropEffect = 'move'; } : undefined}
      onDragLeave={droppable ? () => setOver(false) : undefined}
      onDrop={droppable ? async (e) => {
        e.preventDefault(); setOver(false);
        const convId = e.dataTransfer.getData('conv-id');
        if (convId) await onDropConv(convId, com.id);
      } : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: 14, padding: 14,
        background: over ? `${com.color}18` : 'var(--sf)',
        borderLeft: `3px solid ${com.color}`,
        cursor: 'pointer',
        transition: 'background 0.15s',
      }}
    >
      {/* Logo/icon area — click to upload */}
      <button
        onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
        data-testid={`community-logo-btn-${com.id}`}
        title="Cliquer pour changer le logo"
        style={{
          width: 48, height: 48, borderRadius: 14, flexShrink: 0,
          background: com.logo ? '#fff' : `${com.color}18`,
          border: `1.5px solid ${com.color}55`,
          padding: 0, cursor: 'pointer',
          position: 'relative', overflow: 'hidden',
          display: 'grid', placeItems: 'center',
        }}
      >
        {uploading ? (
          <Loader2 size={16} color={com.color} className="spin" />
        ) : com.logo ? (
          <img
            src={com.logo}
            alt={com.name}
            data-testid={`community-logo-img-${com.id}`}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Icon size={20} color={com.color} strokeWidth={1.7} />
        )}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        data-testid={`community-logo-input-${com.id}`}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ''; }}
        style={{ display: 'none' }}
      />

      <button
        onClick={onOpen}
        data-testid={`community-open-${com.id}`}
        style={{
          flex: 1, display: 'flex', alignItems: 'center', gap: 14,
          background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer',
          minWidth: 0,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '1rem', color: 'var(--t1)', letterSpacing: '-0.005em' }}>
            {com.name}
          </div>
          <div style={{ fontFamily: 'Lexend', fontSize: '0.74rem', color: 'var(--t3)', marginTop: 2 }}>
            {count} {count > 1 ? 'groupes' : 'groupe'} · Ouvrir
          </div>
        </div>
        <div style={{
          padding: '3px 10px', borderRadius: 999,
          background: `${com.color}18`, color: com.color,
          fontFamily: 'Sora', fontWeight: 800, fontSize: 11,
        }}>{count}</div>
      </button>
      {onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          data-testid={`community-delete-${com.id}`}
          title="Supprimer la communauté"
          style={{
            padding: 7, borderRadius: 8,
            background: 'var(--sf2)', border: '1.5px solid var(--bd)',
            color: '#FF6B6B', cursor: 'pointer',
          }}
        ><Trash2 size={12} /></button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Iter47s — ManageGroupDialog: rename / add member / delete for group convs
// ---------------------------------------------------------------------------
function ManageGroupDialog({ conv, users, onClose, onRename, onAddMember, onDelete }) {
  const [name, setName] = useState(conv.name || '');
  const [memberQuery, setMemberQuery] = useState('');
  const currentMemberIds = new Set((conv.members || []).map((m) => (typeof m === 'string' ? m : m.id)));
  const candidates = users.filter((u) =>
    !currentMemberIds.has(u.id) &&
    (memberQuery === '' || u.name.toLowerCase().includes(memberQuery.toLowerCase()))
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(6,16,32,0.72)', backdropFilter: 'blur(6px)',
        display: 'grid', placeItems: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="manage-group-dialog"
        style={{
          background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 14,
          width: '100%', maxWidth: 480,
          boxShadow: '0 24px 64px rgba(0,0,0,0.55)',
          overflow: 'hidden',
        }}
      >
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--bd)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            width: 34, height: 34, borderRadius: 9,
            background: 'rgba(167,139,250,0.16)',
            border: '1.5px solid rgba(167,139,250,0.45)',
            display: 'grid', placeItems: 'center',
          }}><UsersIcon size={15} color="#A78BFA" /></div>
          <div style={{ flex: 1, fontFamily: 'Sora', fontWeight: 800, fontSize: 15, color: 'var(--t1)' }}>
            Gérer le groupe
          </div>
          <button onClick={onClose} style={{
            width: 28, height: 28, borderRadius: 7,
            background: 'transparent', border: '1px solid var(--bd)',
            color: 'var(--t3)', display: 'grid', placeItems: 'center', cursor: 'pointer',
          }}><X size={12} /></button>
        </div>

        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Rename */}
          <div>
            <label style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: 'var(--t2)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Nom du groupe
            </label>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <input
                data-testid="manage-group-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') onRename(conv, name); }}
                style={{
                  flex: 1, padding: '10px 12px',
                  background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 9,
                  fontFamily: 'Lexend', fontSize: 13, color: 'var(--t1)',
                }}
              />
              <button
                data-testid="manage-group-rename-btn"
                onClick={() => onRename(conv, name)}
                disabled={!name.trim() || name.trim() === conv.name}
                style={{
                  padding: '10px 14px', borderRadius: 9,
                  background: 'var(--p)', color: '#fff', border: 'none',
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
                  cursor: (name.trim() && name.trim() !== conv.name) ? 'pointer' : 'not-allowed',
                  opacity: (name.trim() && name.trim() !== conv.name) ? 1 : 0.5,
                }}
              ><Check size={12} /></button>
            </div>
          </div>

          {/* Add member */}
          <div>
            <label style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: 'var(--t2)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Ajouter un membre
            </label>
            <input
              data-testid="manage-group-search-member"
              placeholder="Rechercher…"
              value={memberQuery}
              onChange={(e) => setMemberQuery(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', marginTop: 6,
                background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 9,
                fontFamily: 'Lexend', fontSize: 13, color: 'var(--t1)',
              }}
            />
            <div style={{ maxHeight: 200, overflowY: 'auto', marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {candidates.length === 0 && (
                <div style={{ padding: 10, fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', textAlign: 'center' }}>
                  Aucun utilisateur disponible
                </div>
              )}
              {candidates.slice(0, 20).map((u) => (
                <button
                  key={u.id}
                  data-testid={`manage-group-add-${u.id}`}
                  onClick={() => onAddMember(conv, u.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 10px', borderRadius: 8,
                    background: 'var(--sf2)', border: '1px solid var(--bd)',
                    fontFamily: 'Sora', fontWeight: 600, fontSize: 12.5,
                    color: 'var(--t1)', cursor: 'pointer', textAlign: 'left',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#5BBEFC'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--bd)'; }}
                >
                  <Avatar user={u} size={22} />
                  <span style={{ flex: 1 }}>{u.name}</span>
                  <UserPlus size={12} color="#5BBEFC" />
                </button>
              ))}
            </div>
          </div>

          {/* Delete */}
          <button
            data-testid="manage-group-delete-btn"
            onClick={() => onDelete(conv)}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 9,
              background: 'rgba(255,107,107,0.12)',
              border: '1.5px solid rgba(255,107,107,0.45)',
              color: '#FF6B6B',
              fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
              cursor: 'pointer',
            }}
          ><Trash2 size={13} /> Supprimer le groupe</button>
        </div>
      </div>
    </div>
  );
}

function ConvRow_OLD_REPLACED_BELOW({ conv, community, onClick, onAssign }) {
  const isGroup = conv.type === 'group';
  return (
    <div
      className="asa-card row-card"
      draggable={isGroup}
      onDragStart={isGroup ? (e) => {
        e.dataTransfer.setData('conv-id', conv.id);
        e.dataTransfer.effectAllowed = 'move';
      } : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: 14, padding: 14,
        textAlign: 'left',
        borderLeft: community ? `3px solid ${community.color}` : undefined,
        cursor: isGroup ? 'grab' : 'pointer',
      }}
    >
      <button
        onClick={onClick}
        data-testid={`conv-${conv.id}`}
        style={{
          flex: 1, display: 'flex', alignItems: 'center', gap: 14,
          background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer',
          minWidth: 0,
        }}
      >
        {isGroup ? (
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: community ? `${community.color}14` : 'rgba(167,139,250,0.12)',
            border: `1.5px solid ${community ? `${community.color}55` : 'rgba(167,139,250,0.4)'}`,
            display: 'grid', placeItems: 'center', flexShrink: 0,
          }}>
            <UsersIcon size={17} color={community ? community.color : '#A78BFA'} strokeWidth={1.7} />
          </div>
        ) : (
          <Avatar user={conv.with} size={44} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: '0.92rem', color: 'var(--t1)' }}>
              {conv.name}
            </span>
            {community && (
              <span
                data-testid={`conv-${conv.id}-community-badge`}
                style={{
                  fontFamily: 'Sora', fontWeight: 700, fontSize: '0.56rem',
                  color: community.color, padding: '2px 7px', borderRadius: 999,
                  background: `${community.color}14`,
                  border: `1px solid ${community.color}55`,
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                }}>{community.name}</span>
            )}
            {isGroup && (
              <span style={{
                fontFamily: 'Sora', fontWeight: 600, fontSize: '0.58rem',
                color: '#A78BFA', padding: '2px 7px', borderRadius: 999,
                background: 'rgba(167,139,250,0.12)', textTransform: 'uppercase', letterSpacing: '0.06em',
              }}>Groupe · {conv.members?.length || 0}</span>
            )}
          </div>
          <div style={{
            fontFamily: 'Lexend', fontSize: '0.78rem', color: 'var(--t3)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 3,
          }}>
            {conv.last?.text || 'Aucun message — démarrer la conversation'}
          </div>
        </div>
        {conv.last && (
          <div style={{ fontFamily: 'Lexend', fontSize: '0.66rem', color: 'var(--t3)' }}>
            {formatRelative(conv.last.created_at)}
          </div>
        )}
      </button>

      {onAssign && (
        <button
          onClick={(e) => { e.stopPropagation(); onAssign(); }}
          data-testid={`conv-${conv.id}-assign`}
          title="Affecter à une communauté"
          style={{
            padding: 7, borderRadius: 8,
            background: 'var(--sf2)', border: '1.5px solid var(--bd)',
          }}
        >
          <MoreVertical size={13} color="var(--t3)" />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CommunityDialog — create a new community
// ---------------------------------------------------------------------------
function CommunityDialog({ onClose, onSaved }) {
  const [name, setName] = useState('');
  const [color, setColor] = useState(COMMUNITY_COLORS[0]);
  const [icon, setIcon] = useState('users');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) { toast.error('Nom requis'); return; }
    setBusy(true);
    try {
      await api.post('/communities', { name: name.trim(), color, icon });
      toast.success('Communauté créée');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(10,26,51,0.70)', backdropFilter: 'blur(8px)',
        display: 'grid', placeItems: 'center', padding: 18,
      }}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="scale-in"
        style={{
          width: '100%', maxWidth: 440,
          background: 'var(--sf)', borderRadius: 22,
          border: '1.5px solid var(--bd)', padding: 24,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18 }}>
          <h2 className="asa-heading" style={{ margin: 0, fontSize: 'var(--text-xl)', flex: 1 }}>
            Nouvelle communauté
          </h2>
          <button type="button" onClick={onClose}><X size={18} color="var(--t3)" /></button>
        </div>

        <div className="asa-eyebrow" style={{ marginBottom: 8 }}>Nom</div>
        <input
          data-testid="community-name-input"
          autoFocus
          placeholder="Ex: Nash, Yves, Internes…"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{
            width: '100%', padding: '12px 14px',
            background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12,
            fontFamily: 'Lexend', fontSize: '0.95rem', color: 'var(--t1)',
          }}
        />

        <div className="asa-eyebrow" style={{ marginTop: 14, marginBottom: 8 }}>Couleur</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {COMMUNITY_COLORS.map((c) => (
            <button
              key={c} type="button"
              onClick={() => setColor(c)}
              style={{
                width: 32, height: 32, borderRadius: '50%', background: c,
                border: color === c ? '3px solid var(--t1)' : '3px solid transparent',
                cursor: 'pointer',
              }}
            />
          ))}
        </div>

        <div className="asa-eyebrow" style={{ marginTop: 14, marginBottom: 8 }}>Icône</div>
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
          {COMMUNITY_ICON_KEYS.map((k) => {
            const Ic = COMMUNITY_ICONS[k];
            const active = icon === k;
            return (
              <button
                key={k} type="button"
                onClick={() => setIcon(k)}
                style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: active ? `${color}26` : 'var(--sf2)',
                  border: `1.5px solid ${active ? color : 'var(--bd)'}`,
                  display: 'grid', placeItems: 'center',
                }}
              >
                <Ic size={15} color={active ? color : 'var(--t3)'} strokeWidth={1.7} />
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
          <button type="button" onClick={onClose} className="asa-btn-ghost asa-btn" style={{ flex: 1 }}>Annuler</button>
          <button data-testid="community-submit" type="submit" disabled={busy} className="asa-btn" style={{ flex: 1 }}>
            {busy ? <Loader2 size={14} className="spin" /> : 'Créer'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AssignToCommunityDialog — quickly drop a conv into a community
// ---------------------------------------------------------------------------
function AssignToCommunityDialog({ convId, communities, currentCommunityId, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);

  const assign = async (cid) => {
    setBusy(true);
    try {
      await api.patch(`/conversations/${convId}/community`, { community_id: cid });
      toast.success(cid ? 'Communauté assignée' : 'Retiré de la communauté');
      onSaved();
    } catch (e) { toast.error('Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(10,26,51,0.70)', backdropFilter: 'blur(8px)',
        display: 'grid', placeItems: 'center', padding: 18,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="scale-in"
        style={{
          width: '100%', maxWidth: 400,
          background: 'var(--sf)', borderRadius: 22,
          border: '1.5px solid var(--bd)', padding: 22,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
          <h2 className="asa-heading" style={{ margin: 0, fontSize: 'var(--text-lg)', flex: 1 }}>
            Choisir une communauté
          </h2>
          <button type="button" onClick={onClose}><X size={18} color="var(--t3)" /></button>
        </div>

        <button
          onClick={() => assign(null)}
          disabled={busy}
          data-testid="assign-com-none"
          style={{
            width: '100%', padding: '10px 14px', marginBottom: 8,
            background: !currentCommunityId ? 'rgba(255,255,255,0.06)' : 'var(--sf2)',
            border: `1.5px solid ${!currentCommunityId ? 'var(--t3)' : 'var(--bd)'}`,
            borderRadius: 11, textAlign: 'left',
            fontFamily: 'Sora', fontWeight: 600, fontSize: 13, color: 'var(--t1)',
            cursor: 'pointer',
          }}
        >Aucune (libre)</button>

        {communities.map((com) => {
          const Ic = COMMUNITY_ICONS[com.icon] || UsersIcon;
          const active = currentCommunityId === com.id;
          return (
            <button
              key={com.id}
              onClick={() => assign(com.id)}
              disabled={busy}
              data-testid={`assign-com-${com.id}`}
              style={{
                width: '100%', padding: '10px 14px', marginBottom: 8,
                display: 'flex', alignItems: 'center', gap: 10,
                background: active ? `${com.color}1F` : 'var(--sf2)',
                border: `1.5px solid ${active ? com.color : 'var(--bd)'}`,
                borderRadius: 11, textAlign: 'left',
                fontFamily: 'Sora', fontWeight: 600, fontSize: 13,
                color: active ? com.color : 'var(--t1)',
                cursor: 'pointer',
              }}
            >
              <div style={{
                width: 30, height: 30, borderRadius: '50%',
                background: `${com.color}26`, border: `1.5px solid ${com.color}66`,
                display: 'grid', placeItems: 'center',
              }}>
                <Ic size={14} color={com.color} />
              </div>
              {com.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Thread (supports both DM and group)
// ---------------------------------------------------------------------------

// Iter60d — collapsed 3-dots menu for group conversation header (fixes mobile overflow)
function ConvHeaderMenu({ conv, onRename, onArchive, onDelete }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button
        data-testid="conv-actions-menu"
        onClick={() => setOpen((v) => !v)}
        title="Actions"
        style={{
          width: 32, height: 32, borderRadius: 8,
          background: 'var(--sf2)', border: '1.5px solid var(--bd)',
          color: 'var(--t2)', cursor: 'pointer',
          display: 'grid', placeItems: 'center',
        }}
      >
        <MoreVertical size={14} />
      </button>
      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 500 }}
          />
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 501,
              background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 10,
              padding: 4, minWidth: 200, boxShadow: '0 12px 32px rgba(0,0,0,0.4)',
            }}
          >
            <MenuItem
              testid="conv-rename"
              icon={<Pencil size={12} />}
              label="Renommer le groupe"
              onClick={() => { setOpen(false); onRename(); }}
            />
            <MenuItem
              testid="conv-archive"
              icon={<Archive size={12} />}
              label="Archiver le groupe"
              onClick={() => { setOpen(false); onArchive(); }}
            />
            <MenuItem
              testid="conv-delete"
              icon={<Trash2 size={12} />}
              label="Supprimer le groupe"
              danger
              onClick={() => { setOpen(false); onDelete(); }}
            />
          </div>
        </>
      )}
    </div>
  );
}


function Thread({ conv, currentUserId, onBack }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const isGroup = conv.type === 'group';

  const load = async () => {
    try {
      const url = isGroup
        ? `/conversations/${conv.id}/messages`
        : `/messages/${conv.with.id}`;
      const r = await api.get(url);
      setMsgs(r.data);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
    /* eslint-disable-next-line */
  }, [conv.id]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      if (isGroup) {
        await api.post('/conversations/message', { conversation_id: conv.id, text: text.trim() });
      } else {
        await api.post('/messages', { to: conv.with.id, text: text.trim() });
      }
      setText('');
      await load();
    } finally { setBusy(false); }
  };

  const archive = async () => {
    if (!window.confirm(`Archiver le groupe "${conv.name}" ?`)) return;
    try {
      await api.patch(`/conversations/${conv.id}`, { archived: true });
      toast.success('Groupe archivé');
      onBack();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const rename = async () => {
    const newName = window.prompt('Nouveau nom du groupe :', conv.name);
    if (!newName || !newName.trim() || newName.trim() === conv.name) return;
    try {
      await api.patch(`/conversations/${conv.id}`, { name: newName.trim() });
      toast.success('Groupe renommé');
      // Optimistic: mutate local title
      conv.name = newName.trim();
      // Trigger re-render by forcing a lightweight state update via load
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const remove = async () => {
    if (!window.confirm(`Supprimer définitivement le groupe "${conv.name}" ?\n\nL'historique des messages sera perdu.`)) return;
    try {
      await api.delete(`/conversations/${conv.id}`);
      toast.success('Groupe supprimé');
      onBack();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 78px)' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '14px 18px', borderBottom: '1.5px solid var(--bd)',
        position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 5,
      }}>
        <button onClick={onBack} style={{ padding: 6 }}><ArrowLeft size={18} color="var(--t3)" /></button>

        {isGroup ? (
          <>
            <div style={{
              width: 38, height: 38, borderRadius: 11,
              background: 'rgba(167,139,250,0.14)', border: '1.5px solid rgba(167,139,250,0.4)',
              display: 'grid', placeItems: 'center',
            }}>
              <UsersIcon size={14} color="#A78BFA" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: '0.92rem', color: 'var(--t1)' }}>
                {conv.name}
              </div>
              <div style={{ fontFamily: 'Lexend', fontSize: '0.7rem', color: 'var(--t3)' }}>
                {(conv.members || []).map((m) => m.name?.split(' ')[0]).join(' · ')}
              </div>
            </div>
            <ConvHeaderMenu
              conv={conv}
              onRename={rename}
              onArchive={archive}
              onDelete={remove}
            />
          </>
        ) : (
          <>
            <Avatar user={conv.with} size={36} />
            <div>
              <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: '0.92rem', color: 'var(--t1)' }}>{conv.with.name}</div>
              <div style={{ fontFamily: 'Lexend', fontSize: '0.7rem', color: 'var(--t3)' }}>
                {conv.with.role === 'founder' ? 'Founder' : conv.with.role === 'gestionnaire' ? 'Gestionnaire' : 'Editor'}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Iter43g — Pinned messages banner (WhatsApp-like sticky at top of thread) */}
      {(() => {
        const pinned = msgs.filter((m) => m.pinned && !m.deleted);
        if (pinned.length === 0) return null;
        return (
          <div data-testid="pinned-banner" style={{
            padding: '10px 16px',
            background: 'rgba(245,158,11,0.10)',
            borderBottom: '1.5px solid rgba(245,158,11,0.35)',
            display: 'flex', flexDirection: 'column', gap: 6,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13 }}>📌</span>
              <span style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 10, letterSpacing: '0.06em', color: '#F59E0B', textTransform: 'uppercase' }}>
                Messages épinglés · {pinned.length}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {pinned.map((m) => {
                const sender = isGroup ? (conv.members || []).find((u) => u.id === m.from) : null;
                return (
                  <button
                    key={m.id}
                    data-testid={`pinned-item-${m.id}`}
                    type="button"
                    onClick={() => {
                      // scroll to the message
                      const el = document.getElementById(`msg-${m.id}`);
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }}
                    style={{
                      display: 'flex', gap: 8, alignItems: 'flex-start',
                      padding: '6px 8px', borderRadius: 8,
                      background: 'var(--sf)', border: '1px solid rgba(245,158,11,0.35)',
                      cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 10, color: sender?.color || 'var(--t2)', flexShrink: 0 }}>
                      {sender?.name?.split(' ')[0] || 'Toi'}
                    </span>
                    <span style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t2)', lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {m.text}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}

      <div style={{ flex: 1, padding: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {msgs.map((m, idx) => {
          const mine = m.from === currentUserId;
          const sender = isGroup ? (conv.members || []).find((u) => u.id === m.from) : null;
          const showAuthor = isGroup && !mine && (idx === 0 || msgs[idx - 1].from !== m.from);
          return (
            <div key={m.id} id={`msg-${m.id}`}>
              <MessageBubble
                m={m}
                mine={mine}
                sender={sender}
                showAuthor={showAuthor}
                onEdited={(updated) => setMsgs((prev) => prev.map((x) => x.id === updated.id ? updated : x))}
                onDeleted={(mid) => setMsgs((prev) => prev.map((x) => x.id === mid ? { ...x, deleted: true, text: '' } : x))}
              />
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="msg-input-wrap" style={{
        position: 'sticky', bottom: 0,
        padding: '12px 16px', background: 'var(--bg)',
        borderTop: '1.5px solid var(--bd)',
        display: 'flex', gap: 8, alignItems: 'flex-end',
      }}>
        <div className="asa-field" style={{ flex: 1, padding: '8px 12px' }}>
          <textarea
            data-testid="msg-input"
            placeholder="Message… (Shift+Entrée pour saut de ligne)"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send(e);
              }
            }}
            rows={1}
            style={{
              width: '100%', fontSize: '0.88rem',
              resize: 'none', minHeight: 22, maxHeight: 160,
              lineHeight: 1.4, background: 'transparent',
              border: 'none', color: 'var(--t1)',
              fontFamily: 'Lexend',
            }}
          />
        </div>
        <button
          data-testid="msg-send"
          type="submit"
          disabled={!text.trim() || busy}
          className="asa-btn"
          style={{ width: 46, height: 46, padding: 0, borderRadius: 12, flexShrink: 0 }}
        >
          <Send size={16} />
        </button>
      </form>
    </div>
  );
}

function NewGroupDialog({ users, communities = [], onClose, onCreated }) {
  const [name, setName] = useState('');
  const [members, setMembers] = useState([]);
  const [communityId, setCommunityId] = useState(null);
  const [busy, setBusy] = useState(false);

  const toggle = (id) =>
    setMembers((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) {
      toast.error('Nom du groupe requis');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post('/conversations/group', {
        name: name.trim(), members, community_id: communityId || null,
      });
      toast.success('Groupe créé');
      onCreated(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(10,26,51,0.70)', backdropFilter: 'blur(8px)',
      display: 'grid', placeItems: 'center', padding: 18,
    }}>
      <form
        onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="scale-in"
        style={{
          width: '100%', maxWidth: 440,
          maxHeight: '90vh',
          background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 22, padding: 22,
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18, flexShrink: 0 }}>
          <h2 className="asa-heading" style={{ margin: 0, fontSize: 'var(--text-xl)', flex: 1 }}>
            Nouveau groupe
          </h2>
          <button type="button" onClick={onClose}><X size={18} color="var(--t3)" /></button>
        </div>

        {/* Iter 62bw — scrollable middle section so submit button stays visible */}
        <div style={{ flex: '1 1 auto', overflowY: 'auto', marginBottom: 16, paddingRight: 4 }}>
        <div className="asa-eyebrow" style={{ marginBottom: 8 }}>Nom du groupe</div>
        <input
          data-testid="group-name"
          autoFocus placeholder="Ex: Montage Podcast Hakim"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{
            width: '100%', padding: '11px 14px',
            background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12,
            fontFamily: 'Lexend', fontSize: '0.92rem', color: 'var(--t1)',
            marginBottom: 16,
          }}
        />

        <div className="asa-eyebrow" style={{ marginBottom: 8 }}>Membres ({members.length})</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 18 }}>
          {users.map((u) => {
            const active = members.includes(u.id);
            return (
              <button
                key={u.id}
                type="button"
                onClick={() => toggle(u.id)}
                data-testid={`group-member-${u.id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '9px 12px', borderRadius: 11,
                  background: active ? 'rgba(46,163,242,0.10)' : 'var(--sf2)',
                  border: `1.5px solid ${active ? 'rgba(46,163,242,0.4)' : 'var(--bd)'}`,
                  textAlign: 'left',
                }}
              >
                <Avatar user={u} size={30} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'Sora', fontWeight: 600, fontSize: '0.85rem', color: 'var(--t1)' }}>{u.name}</div>
                  <div style={{ fontFamily: 'Lexend', fontSize: '0.66rem', color: 'var(--t3)' }}>
                    {u.role === 'founder' ? 'Founder' : u.role === 'gestionnaire' ? 'Gestionnaire' : 'Editor'}
                  </div>
                </div>
                <div style={{
                  width: 20, height: 20, borderRadius: 5,
                  background: active ? 'var(--p)' : 'transparent',
                  border: `1.5px solid ${active ? 'var(--p)' : 'var(--bd)'}`,
                  display: 'grid', placeItems: 'center',
                }}>
                  {active && <div style={{ width: 8, height: 8, borderRadius: 2, background: '#fff' }} />}
                </div>
              </button>
            );
          })}
        </div>

        {communities.length > 0 && (
          <>
            <div className="asa-eyebrow" style={{ marginBottom: 8 }}>Communauté (optionnel)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18 }}>
              <button
                type="button"
                onClick={() => setCommunityId(null)}
                style={{
                  padding: '6px 11px', borderRadius: 999,
                  background: communityId === null ? 'var(--p)' : 'var(--sf2)',
                  border: `1.5px solid ${communityId === null ? 'var(--p)' : 'var(--bd)'}`,
                  color: communityId === null ? '#fff' : 'var(--t2)',
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                }}
              >Aucune</button>
              {communities.map((com) => {
                const active = communityId === com.id;
                return (
                  <button
                    key={com.id}
                    type="button"
                    data-testid={`group-community-${com.id}`}
                    onClick={() => setCommunityId(com.id)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '6px 11px', borderRadius: 999,
                      background: active ? `${com.color}26` : 'var(--sf2)',
                      border: `1.5px solid ${active ? com.color : 'var(--bd)'}`,
                      color: active ? com.color : 'var(--t2)',
                      fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                    }}
                  >
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: com.color }} />
                    {com.name}
                  </button>
                );
              })}
            </div>
          </>
        )}
        </div>

        <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
          <button type="button" onClick={onClose} className="asa-btn-ghost asa-btn" style={{ flex: 1 }}>
            Annuler
          </button>
          <button
            data-testid="group-submit"
            type="submit"
            disabled={busy || !name.trim()}
            className="asa-btn"
            style={{ flex: 1 }}
          >
            {busy ? <Loader2 size={15} className="spin" /> : 'Créer le groupe'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Linkified — turns URLs in plain text into clickable anchors
// ---------------------------------------------------------------------------
const URL_RE = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;


// ============================================================================
// Iter43d — MessageBubble : WhatsApp-like bubble with hover edit/delete actions
// ============================================================================
function MessageBubble({ m, mine, sender, showAuthor, onEdited, onDeleted }) {
  const [hovered, setHovered] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(m.text || '');
  const [showEmojis, setShowEmojis] = useState(false);

  const isDeleted = !!m.deleted;
  const isEdited = !!m.edited_at;
  const isPinned = !!m.pinned;
  const reactions = m.reactions || {};

  const saveEdit = async () => {
    const val = draft.trim();
    if (!val) return;
    try {
      const r = await api.patch(`/messages/${m.id}`, { text: val });
      onEdited?.(r.data);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const removeMsg = async () => {
    if (!window.confirm('Supprimer ce message ?')) return;
    try {
      await api.delete(`/messages/${m.id}`);
      onDeleted?.(m.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const toggleReaction = async (emoji) => {
    try {
      const r = await api.post(`/messages/${m.id}/reactions`, { emoji });
      onEdited?.(r.data);
      setShowEmojis(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const togglePin = async () => {
    try {
      const r = await api.post(`/messages/${m.id}/pin`);
      onEdited?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const QUICK_EMOJIS = ['👍', '❤️', '😂', '🔥', '👏', '🙏'];

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start', position: 'relative' }}
    >
      {showAuthor && sender && (
        <div style={{ fontFamily: 'Sora', fontWeight: 600, fontSize: '0.66rem', color: sender.color, marginBottom: 2, paddingLeft: 4 }}>
          {sender.name.split(' ')[0]}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexDirection: mine ? 'row' : 'row-reverse' }}>
        {hovered && !isDeleted && !editing && (
          <div style={{ display: 'flex', gap: 4, opacity: 0.9, position: 'relative' }}>
            {/* Emoji reaction picker */}
            <button
              data-testid={`msg-react-${m.id}`}
              type="button" onClick={() => setShowEmojis((v) => !v)}
              title="Réagir"
              style={{
                width: 26, height: 26, borderRadius: 8, display: 'grid', placeItems: 'center',
                background: 'var(--sf2)', border: '1px solid var(--bd)', cursor: 'pointer', fontSize: 12,
              }}
            >😊</button>
            {showEmojis && (
              <div
                data-testid={`msg-emoji-picker-${m.id}`}
                style={{
                  position: 'absolute', top: 30, [mine ? 'right' : 'left']: 0, zIndex: 5,
                  display: 'flex', gap: 3, padding: 5, borderRadius: 10,
                  background: 'var(--sf)', border: '1.5px solid var(--bd)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                }}
              >
                {QUICK_EMOJIS.map((e) => (
                  <button
                    key={e} type="button" onClick={() => toggleReaction(e)}
                    style={{ padding: '3px 6px', borderRadius: 6, fontSize: 16, cursor: 'pointer', background: 'transparent' }}
                    onMouseEnter={(ev) => { ev.currentTarget.style.background = 'var(--sf2)'; }}
                    onMouseLeave={(ev) => { ev.currentTarget.style.background = 'transparent'; }}
                  >{e}</button>
                ))}
              </div>
            )}
            {/* Pin */}
            <button
              data-testid={`msg-pin-${m.id}`}
              type="button" onClick={togglePin}
              title={isPinned ? 'Désépingler' : 'Épingler'}
              style={{
                width: 26, height: 26, borderRadius: 8, display: 'grid', placeItems: 'center',
                background: isPinned ? 'rgba(245,158,11,0.20)' : 'var(--sf2)',
                border: '1px solid ' + (isPinned ? '#F59E0B' : 'var(--bd)'),
                cursor: 'pointer', fontSize: 11,
              }}
            >📌</button>
            {mine && (
              <>
                <button
                  data-testid={`msg-edit-${m.id}`}
                  type="button" onClick={() => { setDraft(m.text || ''); setEditing(true); }}
                  title="Modifier"
                  style={{
                    width: 26, height: 26, borderRadius: 8, display: 'grid', placeItems: 'center',
                    background: 'var(--sf2)', border: '1px solid var(--bd)', cursor: 'pointer',
                  }}
                >
                  <Pencil size={12} color="var(--t3)" />
                </button>
                <button
                  data-testid={`msg-delete-${m.id}`}
                  type="button" onClick={removeMsg}
                  title="Supprimer"
                  style={{
                    width: 26, height: 26, borderRadius: 8, display: 'grid', placeItems: 'center',
                    background: 'var(--sf2)', border: '1px solid var(--bd)', cursor: 'pointer',
                  }}
                >
                  <Trash2 size={12} color="var(--r)" />
                </button>
              </>
            )}
          </div>
        )}
        <div style={{
          maxWidth: 480, minWidth: 60,
          padding: '10px 14px', borderRadius: 14,
          background: isDeleted ? 'var(--sf2)' : (mine ? 'var(--p)' : 'var(--sf)'),
          color: isDeleted ? 'var(--t3)' : (mine ? '#fff' : 'var(--t1)'),
          border: isDeleted ? '1.5px dashed var(--bd)' : (mine ? 'none' : '1.5px solid var(--bd)'),
          fontFamily: 'Lexend', fontSize: '0.86rem', lineHeight: 1.5,
          borderBottomRightRadius: mine ? 4 : 14,
          borderBottomLeftRadius: mine ? 14 : 4,
          wordBreak: 'break-word', whiteSpace: 'pre-wrap',
          fontStyle: isDeleted ? 'italic' : 'normal',
        }}>
          {isDeleted ? (
            '🚫 Message supprimé'
          ) : editing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <textarea
                data-testid={`msg-edit-input-${m.id}`}
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(); }
                  if (e.key === 'Escape') { setEditing(false); setDraft(m.text || ''); }
                }}
                rows={Math.max(2, Math.min(6, (draft.match(/\n/g) || []).length + 2))}
                style={{
                  width: 320, resize: 'vertical', minHeight: 42,
                  background: 'rgba(255,255,255,0.15)', color: '#fff',
                  border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8,
                  padding: '6px 10px', fontFamily: 'Lexend', fontSize: '0.86rem', lineHeight: 1.4,
                }}
              />
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <button
                  type="button" onClick={() => { setEditing(false); setDraft(m.text || ''); }}
                  style={{ fontSize: 10.5, fontFamily: 'Sora', fontWeight: 600, color: 'rgba(255,255,255,0.7)', cursor: 'pointer' }}
                >Annuler</button>
                <button
                  data-testid={`msg-edit-save-${m.id}`}
                  type="button" onClick={saveEdit}
                  style={{
                    fontSize: 10.5, padding: '3px 10px', borderRadius: 999,
                    background: 'rgba(255,255,255,0.25)', color: '#fff',
                    fontFamily: 'Sora', fontWeight: 700, cursor: 'pointer',
                  }}
                >Enregistrer</button>
              </div>
            </div>
          ) : (
            <Linkified text={m.text} mine={mine} />
          )}
          {!editing && (
            <div style={{ fontSize: '0.6rem', opacity: 0.65, marginTop: 4, textAlign: mine ? 'right' : 'left' }}>
              {isPinned && <span style={{ marginRight: 4, color: '#F59E0B' }}>📌</span>}
              {formatRelative(m.created_at)}
              {isEdited && !isDeleted && <span style={{ marginLeft: 4, fontStyle: 'italic' }}>· modifié</span>}
            </div>
          )}
        </div>
      </div>
      {/* Iter43e — Reactions row below bubble */}
      {!isDeleted && Object.keys(reactions).length > 0 && (
        <div
          data-testid={`msg-reactions-${m.id}`}
          style={{
            display: 'flex', gap: 4, marginTop: 4,
            flexDirection: mine ? 'row-reverse' : 'row',
          }}
        >
          {Object.entries(reactions).map(([emoji, uids]) => (
            <button
              key={emoji}
              type="button"
              onClick={() => toggleReaction(emoji)}
              style={{
                padding: '2px 8px', borderRadius: 999,
                background: 'var(--sf2)', border: '1.5px solid var(--bd)',
                fontSize: 12, fontFamily: 'Sora', fontWeight: 700, color: 'var(--t2)',
                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3,
              }}
            >
              <span>{emoji}</span>
              <span style={{ fontSize: 10 }}>{uids.length}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function Linkified({ text, mine }) {
  if (!text) return null;
  const parts = String(text).split(URL_RE);
  return (
    <span style={{ whiteSpace: 'pre-wrap' }}>
      {parts.map((part, i) => {
        if (i % 2 === 1) {
          const href = part.startsWith('http') ? part : `https://${part}`;
          return (
            <a
              key={i}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                color: mine ? '#fff' : 'var(--pl)',
                textDecoration: 'underline',
                textUnderlineOffset: 2,
                wordBreak: 'break-all',
              }}
            >
              {part}
            </a>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
}



// ---------------------------------------------------------------------------
// Iter 62aw — Client picker modal + Create-client-group modal
// Top-level flow : "Créer un groupe client" → pick client → configure group.
// ---------------------------------------------------------------------------
function ClientPickerModal({ onClose, onPick }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/clients').then((r) => {
      setClients(r.data || []); setLoading(false);
    }).catch(() => setLoading(false));
  }, []);
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(3,7,18,0.72)', backdropFilter: 'blur(8px)',
        display: 'grid', placeItems: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="client-picker-modal"
        style={{
          width: '100%', maxWidth: 460,
          background: 'var(--sf)', borderRadius: 18,
          border: '1px solid rgba(245,197,106,0.35)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
          overflow: 'hidden',
        }}
      >
        <div style={{
          padding: '16px 20px',
          background: 'linear-gradient(140deg, rgba(245,197,106,0.14) 0%, transparent 100%)',
          borderBottom: '1px solid rgba(245,197,106,0.25)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{
              fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5,
              color: '#F5C56A', letterSpacing: '0.14em', textTransform: 'uppercase',
            }}>Étape 1 · Choisir un client</div>
            <div style={{
              fontFamily: 'Sora', fontWeight: 800, fontSize: 16,
              color: 'var(--t1)', marginTop: 2,
            }}>Pour quel client ?</div>
          </div>
          <button onClick={onClose} data-testid="client-picker-close" style={{
            width: 30, height: 30, borderRadius: 8,
            background: 'var(--sf2)', border: '1px solid var(--bd)',
            color: 'var(--t2)', cursor: 'pointer',
            display: 'grid', placeItems: 'center',
          }}><X size={13} /></button>
        </div>
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 460, overflowY: 'auto' }}>
          {loading && <div style={{ padding: 20, textAlign: 'center', color: 'var(--t3)' }}>Chargement…</div>}
          {!loading && clients.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--t3)' }}>Aucun client.</div>
          )}
          {clients.map((cli) => {
            const accent = cli.color || '#5BBEFC';
            const initials = (cli.name || '?').split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('');
            return (
              <button
                key={cli.id}
                data-testid={`client-picker-pick-${cli.id}`}
                onClick={() => onPick(cli)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 12px', borderRadius: 12,
                  background: 'var(--sf2)', border: `1px solid ${accent}33`,
                  cursor: 'pointer', textAlign: 'left',
                  transition: 'all 0.12s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${accent}88`; e.currentTarget.style.background = `${accent}0A`; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = `${accent}33`; e.currentTarget.style.background = 'var(--sf2)'; }}
              >
                <span style={{
                  width: 36, height: 36, borderRadius: 10,
                  display: 'grid', placeItems: 'center',
                  background: cli.avatar
                    ? `url(${cli.avatar}) center / cover no-repeat`
                    : `linear-gradient(140deg, ${accent} 0%, rgba(255,255,255,0.15) 50%, ${accent} 100%)`,
                  color: '#0B1220', fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
                  flexShrink: 0,
                }}>{!cli.avatar && initials}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: 'Sora', fontWeight: 800, fontSize: 14,
                    color: 'var(--t1)',
                  }}>{cli.name}</div>
                  <div style={{
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
                    color: 'var(--t3)', marginTop: 2,
                    textTransform: 'uppercase', letterSpacing: '0.08em',
                  }}>{cli.status || 'prospect'}</div>
                </div>
                <span style={{ color: accent, fontFamily: 'Sora', fontWeight: 800, fontSize: 12 }}>→</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CreateClientGroupModal({ client, users, onClose, onCreated }) {
  const accent = client.color || '#5BBEFC';
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  // Iter 62aw — Rayan + client-as-user + Hugo (creator) always pre-selected.
  const defaultIds = useMemo(() => {
    const ids = new Set();
    // Rayan (co-founder)
    if (users.some((u) => u.id === 'rayan')) ids.add('rayan');
    // Hugo (creator) — will be auto-added by backend anyway
    if (users.some((u) => u.id === 'hugo')) ids.add('hugo');
    // Client-as-user (nash, mathis, …)
    const cid = (client.name || '').toLowerCase().split(' ')[0];
    if (users.some((u) => u.id === cid)) ids.add(cid);
    return ids;
  }, [client, users]);
  const [selected, setSelected] = useState(() => {
    const s = {}; defaultIds.forEach((id) => { s[id] = true; }); return s;
  });

  const suggestions = [
    { label: 'Montage vidéo',   color: '#5BBEFC' },
    { label: 'Head of Content', color: '#B58BFF' },
    { label: 'Stratégie',       color: '#F5C56A' },
  ];

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error('Nom du groupe requis'); return; }
    const members = Object.keys(selected).filter((k) => selected[k]);
    // Always ensure rayan + client-user are included even if user tried to uncheck them
    if (users.some((u) => u.id === 'rayan') && !members.includes('rayan')) members.push('rayan');
    const cid = (client.name || '').toLowerCase().split(' ')[0];
    if (users.some((u) => u.id === cid) && !members.includes(cid)) members.push(cid);
    setSaving(true);
    try {
      const r = await api.post('/conversations/group', {
        name: trimmed, members, client_id: client.id, color: accent,
      });
      toast.success(`Groupe "${trimmed}" créé`);
      onCreated?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(3,7,18,0.72)', backdropFilter: 'blur(8px)',
        display: 'grid', placeItems: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="create-client-group-modal"
        style={{
          width: '100%', maxWidth: 460,
          background: 'var(--sf)', borderRadius: 18,
          border: `1px solid ${accent}55`,
          boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
          overflow: 'hidden',
        }}
      >
        <div style={{
          padding: '16px 20px',
          background: `linear-gradient(140deg, ${accent}22 0%, transparent 100%)`,
          borderBottom: `1px solid ${accent}33`,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{
              fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5,
              color: accent, letterSpacing: '0.14em', textTransform: 'uppercase',
            }}>Étape 2 · Groupe pour {client.name}</div>
            <div style={{
              fontFamily: 'Sora', fontWeight: 800, fontSize: 16,
              color: 'var(--t1)', marginTop: 2,
            }}>Créer le groupe</div>
          </div>
          <button onClick={onClose} data-testid="create-client-group-close" style={{
            width: 30, height: 30, borderRadius: 8,
            background: 'var(--sf2)', border: '1px solid var(--bd)',
            color: 'var(--t2)', cursor: 'pointer',
            display: 'grid', placeItems: 'center',
          }}><X size={13} /></button>
        </div>

        <div style={{ padding: '16px 20px' }}>
          <label style={{
            fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
            color: 'var(--t3)', letterSpacing: '0.12em', textTransform: 'uppercase',
          }}>Nom du groupe</label>
          <input
            data-testid="create-client-group-name" autoFocus value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={`ex. ${client.name} Montage vidéo`}
            style={{
              width: '100%', marginTop: 6, padding: '10px 12px',
              borderRadius: 10, border: '1px solid var(--bd)',
              background: 'var(--sf2)', color: 'var(--t1)',
              fontFamily: 'Sora', fontWeight: 600, fontSize: 13, outline: 'none',
            }}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            {suggestions.map((s) => (
              <button
                key={s.label}
                data-testid={`create-client-group-suggest-${s.label}`}
                onClick={() => setName(`${client.name} ${s.label}`)}
                style={{
                  padding: '5px 10px', borderRadius: 999,
                  background: `${s.color}18`, color: s.color,
                  border: `1px solid ${s.color}44`,
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
                  cursor: 'pointer',
                }}
              >{client.name} {s.label}</button>
            ))}
          </div>

          <label style={{
            display: 'block', marginTop: 16,
            fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
            color: 'var(--t3)', letterSpacing: '0.12em', textTransform: 'uppercase',
          }}>Membres ({Object.values(selected).filter(Boolean).length}) · Rayan et {client.name} toujours inclus</label>
          <div style={{
            marginTop: 8, maxHeight: 240, overflowY: 'auto',
            display: 'flex', flexDirection: 'column', gap: 4,
            padding: 4, borderRadius: 10, border: '1px solid var(--bd)', background: 'var(--sf2)',
          }}>
            {users.map((u) => {
              const isSel = !!selected[u.id];
              const locked = defaultIds.has(u.id); // rayan + client can't be unchecked
              return (
                <button
                  key={u.id}
                  data-testid={`create-client-group-member-${u.id}`}
                  onClick={() => { if (locked) { toast.info('Toujours inclus'); return; } setSelected((s) => ({ ...s, [u.id]: !s[u.id] })); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '7px 8px', borderRadius: 8,
                    background: isSel ? `${accent}22` : 'transparent',
                    border: `1px solid ${isSel ? accent : 'transparent'}`,
                    color: 'var(--t1)', textAlign: 'left', cursor: 'pointer',
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
                    opacity: locked ? 0.9 : 1,
                  }}
                >
                  <span style={{
                    width: 22, height: 22, borderRadius: 999,
                    display: 'grid', placeItems: 'center',
                    background: u.color || '#5BBEFC', color: '#0B1220',
                    fontFamily: 'Sora', fontWeight: 800, fontSize: 10,
                    flexShrink: 0,
                  }}>{u.short || u.name?.[0] || '?'}</span>
                  <span style={{ flex: 1 }}>{u.name}</span>
                  {locked && (
                    <span style={{
                      padding: '1px 6px', borderRadius: 999,
                      background: `${accent}22`, color: accent,
                      border: `1px solid ${accent}55`,
                      fontFamily: 'Sora', fontWeight: 800, fontSize: 8.5,
                      textTransform: 'uppercase', letterSpacing: '0.08em',
                    }}>Fixe</span>
                  )}
                  {!locked && isSel && <span style={{ color: accent, fontSize: 11, fontWeight: 800 }}>✓</span>}
                </button>
              );
            })}
          </div>

          <button
            onClick={submit}
            disabled={saving || !name.trim()}
            data-testid="create-client-group-submit"
            style={{
              width: '100%', marginTop: 16,
              padding: '12px', borderRadius: 12,
              background: name.trim()
                ? `linear-gradient(140deg, ${accent} 0%, ${accent}CC 100%)`
                : 'var(--sf2)',
              border: '1px solid ' + (name.trim() ? accent : 'var(--bd)'),
              color: name.trim() ? '#0B1220' : 'var(--t3)',
              fontFamily: 'Sora', fontWeight: 800, fontSize: 13,
              cursor: name.trim() && !saving ? 'pointer' : 'not-allowed',
              opacity: saving ? 0.6 : 1,
            }}
          >{saving ? 'Création…' : 'Créer le groupe'}</button>
        </div>
      </div>
    </div>
  );
}
